<script setup lang="ts">
import { computed, onMounted, ref, useAttrs, watch } from 'vue';
import { bus } from '@sfvine/main-qiankun';
import type { PersonalCenterList } from './type';
import { useMenuStore, useSettingsStore, useUserStore } from '../../stores';
import { onSensorsWebClick } from '../../plugins/sensors';
import { useRouter } from 'vue-router';
import { drawer } from '../../stores/drawer';
import { getPersonalCenterOptions } from './getPersonalCenterOptions';
import type { PersonalCenterOption } from '../../stores/modules/settings/types';
import SvgIcon from '../Icon/SvgIcon.vue';
import { computedAsync } from '@vueuse/core';
import { useLocale } from '../../hook/useLocale';

const { t } = useLocale();
const emits = defineEmits(['switchRole', 'personalCenterClick', 'click', 'change']);
const attrs = useAttrs();
const props = defineProps<{
  /** 组件配置，通过 personalCenter item 传进来的参数 */
  config: PersonalCenterList[0];
  /**
   * 是否纯展示，不触发事件（例如onClick和onChange，因为父级已经监听了）
   * 参考如下示例
   * <BaseContent :config="item">
   *   <el-popover>
   *     xxx
   *     <template #reference>
   *       <BaseContent :config="configWithoutComp" pure />
   *     </template>
   *   </el-popover>
   * </BaseContent>
   */
  pure?: boolean;
}>();

const settingsStore = useSettingsStore();
const menuStore = useMenuStore();
const userStore = useUserStore();
const router = useRouter();

// 系统内部的 options 需要做拦截 get 和 set 方法
const systemSelfGetSetMap = computed(() => {
  return {
    // 角色
    'auth': {
      value: userStore.roleCode,
      setValue(value: any) {
        userStore.switchRole(value);
        emits('switchRole', value);
      },
    },
    // 多语言
    'lang': {
      value: settingsStore.settings.language.value,
      setValue(value: any) {
        settingsStore.settings.language.value = value;
      },
    },
    // 菜单导航
    'menu-mode': {
      value: settingsStore.settings.menu.menuMode,
      setValue(value: any) {
        settingsStore.settings.menu.menuMode = value;
      },
    },
    // 换肤
    'theme': {
      value: settingsStore.settings.theme.style,
      setValue(value: any) {
        settingsStore.settings.theme.style = value;
      },
    },
    // 换肤模式
    'theme-mode': {
      value: settingsStore.settings.theme.mode,
      setValue(value: any) {
        settingsStore.settings.theme.mode = value;

        const item = settingsStore.settings.personalCenter.find((item) => item.id === 'theme-mode');
        const onThemeModeChange = item?.onChange;
        if (onThemeModeChange) {
          onThemeModeChange(
            value,
            {
              title: '个人中心下拉菜单',
              source: 'personalCenter-dropdown',
            },
            item,
          );
        } else {
          if (value === 'dark') {
            // 开启夜间模式
            enableDark(); // enableDark([options]) // 可自定义配置
          } else {
            // 关闭夜间模式
            disableDark();
          }
        }
      },
    },
  };
});

const isOutside = computed(() => props.config.placement?.includes('outside'));
const baseClassName = computed(() => [attrs.class, isOutside.value && !props.pure ? 'ml-16' : '']);
const badge = computed(() => {
  if (typeof props.config.badge === 'function') {
    return props.config.badge();
  }

  return props.config.badge;
});

const options = computedAsync<PersonalCenterOption[]>(() => getPersonalCenterOptions(props.config), []);

const selectSelfCompValue = ref();
const selectCompValue = computed(() => {
  const { defaultValue, id } = props.config;

  // 系统内部情况，存在 store 里面的值（options 和 value 数据源是分开的）
  if (id in systemSelfGetSetMap.value) {
    return systemSelfGetSetMap.value[id]?.value;
  }

  // 用户传入的 options，这里用私有变量存储
  if (selectSelfCompValue.value) {
    return selectSelfCompValue.value;
  }

  // 默认情况
  if (defaultValue != null) {
    return defaultValue;
  }

  // options 中默认 active
  const defaultValueFromOptions = options.value.find((item) => item.active)?.value;
  return defaultValueFromOptions || options.value[0]?.value;
});

/**
 * 处理个人中心菜单click/change事件
 */
const handlePersonalCenterEvent = (
  eventType: string,
  { event, value, target }: { event?: MouseEvent; value?: any; target?: PersonalCenterOption },
) => {
  if (props.pure) {
    return;
  }

  const data = props.config;
  const type = data.id;
  const title = data?.label || '';
  const eventData = {
    title: data.type === 'select' ? `切换${title}: ${target?.label || '下拉框'}` : title,
    source: `personalCenter-${data?.placement || ''}`,
  };

  // 菜单钩子
  if (data && eventType in data) {
    const fn = data[eventType];
    if (typeof fn === 'function') {
      fn(value, eventData, data);
    }
  }

  // 埋点钩子
  // 默认埋点 clickmap 为 default，当个人中心单击元素为 button 时，神策会自动上报埋点
  // 因此这里判断下，防止重复上报
  if (settingsStore.settings.sensorsConfig.isManualReport || data.type !== 'button') {
    onSensorsWebClick(eventData);
  }

  // 广播事件
  emits('personalCenterClick', { type, data });

  // 主子应用通信
  bus.$emit('personalCenterClick', { type, data });
};

/**
 * 单击事件
 */
const handleClick = (event: MouseEvent) => {
  emits('click', event);

  if (props.pure) {
    return;
  }

  const data = { event, ...props.config };
  handlePersonalCenterEvent('onClick', { event });
  // 如果是 undefined 表示未配置，表示默认值 true
  if (data.showSidebar === false) {
    menuStore.setActived(-1);
  }

  switch (props.config.id) {
    case 'system': // 系统设置
      drawer.value = true;
      break;
    case 'logout': // 退出登录
      userStore.logout();
      break;
  }

  if (!data?.jumpUrl) {
    return;
  }

  if (/^(https?|\/\/)/.test(data.jumpUrl)) {
    location.href = data.jumpUrl;
    return;
  }

  router.push(data.jumpUrl);
};

/**
 * select change 事件
 * @param value 当前选中值
 */
const handleChange = (value: any) => {
  emits('change', value);

  if (props.pure) {
    return;
  }

  const target = options.value.find((item) => item.value === value);
  handlePersonalCenterEvent('onChange', { value, target });
  selectSelfCompValue.value = value;
  const { id } = props.config;

  // 系统内部情况，存在 store 里面的值（options 和 value 数据源是分开的）
  if (id in systemSelfGetSetMap.value) {
    systemSelfGetSetMap.value[id]?.setValue(value);
  }
};

let enableDark = () => {};
let disableDark = () => {};

onMounted(async () => {
  const v = await import('@pdfe/sf-dark');
  enableDark = v.enableDark;
  disableDark = v.disableDark;

  const mode = settingsStore.settings.theme.mode;

  const item = settingsStore.settings.personalCenter.find((item) => item.id === 'theme-mode');
  const onThemeModeChange = item?.onChange;
  if (onThemeModeChange) {
    onThemeModeChange(
      mode,
      {
        title: '个人中心下拉菜单',
        source: 'personalCenter-dropdown',
      },
      item,
    );
  } else {
    if (mode === 'dark') {
      enableDark(); // enableDark([options]) // 可自定义配置
    } else {
      disableDark();
    }
  }
});
</script>

<template>
  <!-- 自定义组件 -->
  <component
    v-if="'comp' in config && config.comp"
    :is="config.comp"
    :config="config"
    :class="[baseClassName, isOutside ? '' : 'w-full']"
    v-bind="config.props"
    @click="handleClick"
    @change="handleChange"
  />

  <!-- 分割线 -->
  <el-divider
    v-else-if="config.type === 'divided'"
    :class="[baseClassName]"
    v-bind="config.props"
    @click="handleClick"
  />

  <!-- 按钮 -->
  <el-tooltip v-else-if="config.type === 'button'" v-bind="config.tooltipProps" :disabled="!config.tooltipProps">
    <el-badge
      :class="[baseClassName, isOutside ? [] : ['w-full']]"
      :is-dot="config.isDotBadge"
      :value="badge"
      :hidden="(!badge || badge === 0) && !config.isDotBadge"
      :offset="[-2, 5]"
    >
      <el-button
        :class="[isOutside ? [] : ['w-full', 'min-w-72', 'min-h-32']]"
        v-bind="{ ...config.props, ...(isOutside ? { link: true } : {}) }"
        @click="handleClick"
      >
        <template v-if="config.icon" #icon>
          <SvgIcon v-if="typeof config.icon === 'string'" :name="config.icon" />
          <component v-else :is="config.icon" />
        </template>
        {{ config.label }}
      </el-button>
    </el-badge>
  </el-tooltip>

  <!-- 下拉框 -->
  <div v-else-if="config.type === 'select'" :class="[baseClassName, 'flex']" @click="handleClick">
    <span v-if="config.label" class="mr-16">{{ config.label }}</span>
    <el-select class="w-198" :model-value="selectCompValue" v-bind="config.props" @change="handleChange">
      <el-option
        v-for="subItem in options"
        :key="subItem.value"
        :label="t(subItem.label)"
        :value="subItem.value"
        :disabled="subItem.disabled"
      />
    </el-select>
  </div>

  <!-- 其他情况 -->
  <!-- 这里一般不扩展其他组件了，如果有特殊需求，请联系用户使用 comp 自定义组件形式 -->
  <div v-else :class="[baseClassName, isOutside ? '' : 'w-full']" v-bind="config.props" @click="handleClick"
    >暂不支持 {{ config.type }} 类型</div
  >
</template>
<style scoped lang="scss">
@import '../../styles/var.scss';

.ml-16 {
  margin-left: 16px;
}

.mr-16 {
  margin-right: 16px;
}

.w-full {
  width: 100%;
}

.m-y-12 {
  margin-top: 12px;
  margin-bottom: 12px;
}

.min-w-72 {
  min-width: 72px;
}

.min-h-32 {
  min-height: 32px;
}

.flex {
  display: flex;
  align-items: center;
}

.w-198 {
  width: 198px;
}
</style>
